import { ReactNode, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Printer,
  Share2,
  Clock,
  ListOrdered,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  ChevronLeft,
  Mail,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import { toPersianDigits } from "@/lib/auth/utils";
import { toast } from "sonner";

export interface LegalSection {
  id: string;
  title: string;
  /** Lucide icon component */
  icon: React.ComponentType<{ className?: string; size?: number }>;
  /** Bullet points OR plain paragraphs */
  body: ReactNode;
  /** Optional callout shown at top of card */
  highlight?: string;
}

export interface LegalLayoutProps {
  kind: "privacy" | "terms";
  title: string;
  eyebrow: string;
  intro: string;
  updatedAt: string; // persian formatted
  effectiveAt: string;
  sections: LegalSection[];
  /** Link to sister doc */
  sisterTitle: string;
  sisterHref: "/privacy" | "/terms";
  sisterDescription: string;
}

export default function LegalLayout({
  kind,
  title,
  eyebrow,
  intro,
  updatedAt,
  effectiveAt,
  sections,
  sisterTitle,
  sisterHref,
  sisterDescription,
}: LegalLayoutProps) {
  const [active, setActive] = useState(sections[0]?.id);
  const [progress, setProgress] = useState(0);

  // Reading-time estimate from text content length (rough)
  const readingMinutes = useMemo(() => {
    const total = sections.reduce((acc, s) => acc + s.title.length + 220, 0);
    return Math.max(3, Math.round(total / 900));
  }, [sections]);

  // Track active section + scroll progress
  useEffect(() => {
    const ids = sections.map((s) => s.id);
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => {
      window.removeEventListener("scroll", onScroll);
      obs.disconnect();
    };
  }, [sections]);

  const accent =
    kind === "privacy"
      ? "from-emerald-500 via-teal-500 to-cyan-500"
      : "from-amber-500 via-orange-500 to-rose-500";
  const accentSoft =
    kind === "privacy"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : "bg-amber-500/10 text-amber-600 dark:text-amber-400";

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("لینک صفحه کپی شد");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div dir="rtl" className="relative min-h-screen bg-background text-foreground">
      {/* Top progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-0.5 bg-transparent">
        <div
          className={`h-full bg-gradient-to-l ${accent} transition-[width] duration-150`}
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link href="/" className="flex items-center gap-2.5">
            <BrandMark size={36} />
            <div className="hidden flex-col leading-tight sm:flex">
              <span className="text-sm font-extrabold">ترندی اسمارت</span>
              <span className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground">
                TRENDI · SMART
              </span>
            </div>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            <Link
              href="/privacy"
              className="rounded-full px-3 py-1.5 text-xs font-bold text-muted-foreground transition hover:text-foreground"
            >
              حریم خصوصی
            </Link>
            <Link
              href="/terms"
              className="rounded-full px-3 py-1.5 text-xs font-bold text-muted-foreground transition hover:text-foreground"
            >
              قوانین و مقررات
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="hidden h-9 w-9 items-center justify-center rounded-full border border-border/70 text-muted-foreground transition hover:border-primary/40 hover:text-primary md:inline-flex"
              aria-label="چاپ"
            >
              <Printer className="h-4 w-4" />
            </button>
            <button
              onClick={handleShare}
              className="hidden h-9 w-9 items-center justify-center rounded-full border border-border/70 text-muted-foreground transition hover:border-primary/40 hover:text-primary md:inline-flex"
              aria-label="اشتراک‌گذاری"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <ThemeToggle />
            <Link
              href="/"
              className="hidden items-center gap-1 rounded-full border border-border/70 px-3 py-1.5 text-xs font-bold text-muted-foreground transition hover:border-primary/40 hover:text-primary sm:inline-flex"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              بازگشت به سایت
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="pointer-events-none absolute inset-0">
          <div
            className={`absolute -top-32 right-1/4 h-[420px] w-[420px] rounded-full bg-gradient-to-br ${accent} opacity-20 blur-[120px]`}
          />
          <div className="absolute -bottom-32 left-1/4 h-[420px] w-[420px] rounded-full bg-primary/15 blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-3xl text-center"
          >
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold ${accentSoft}`}
            >
              <Sparkles className="h-3 w-3" />
              {eyebrow}
            </span>
            <h1 className="mt-5 text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {title.split(" ").map((w, i) => (
                <span key={i} className="inline-block">
                  {w}&nbsp;
                </span>
              ))}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              {intro}
            </p>

            {/* Meta strip */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs">
              <Chip
                icon={<Clock className="h-3.5 w-3.5" />}
                label={`زمان مطالعه: ${toPersianDigits(readingMinutes)} دقیقه`}
              />
              <Chip
                icon={<ListOrdered className="h-3.5 w-3.5" />}
                label={`${toPersianDigits(sections.length)} بند`}
              />
              <Chip
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                label={`آخرین بروزرسانی: ${updatedAt}`}
              />
              <Chip
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                label={`تاریخ اجرا: ${effectiveAt}`}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-[260px_minmax(0,1fr)]">
          {/* TOC */}
          <aside className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:self-start lg:overflow-y-auto">
            <div className="rounded-2xl border border-border/60 bg-card/40 p-4 backdrop-blur-md">
              <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                <ListOrdered className="h-3.5 w-3.5" />
                فهرست بندها
              </div>
              <ol className="space-y-0.5">
                {sections.map((s, i) => {
                  const isActive = active === s.id;
                  return (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`group flex items-center gap-2 rounded-xl px-2.5 py-2 text-[12.5px] font-bold transition ${
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <span
                          className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-[10px] font-black ${
                            isActive
                              ? `bg-gradient-to-br ${accent} text-white`
                              : "bg-muted text-muted-foreground group-hover:text-foreground"
                          }`}
                        >
                          {toPersianDigits(i + 1)}
                        </span>
                        <span className="line-clamp-1">{s.title}</span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* Sister doc card */}
            <Link
              href={sisterHref}
              className="mt-4 block rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 to-secondary-main/5 p-4 transition hover:border-primary/40"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  بعدی
                </span>
                <ArrowLeft className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="mt-2 text-sm font-extrabold">{sisterTitle}</div>
              <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                {sisterDescription}
              </div>
            </Link>
          </aside>

          {/* Sections */}
          <main className="min-w-0 space-y-6">
            {sections.map((s, i) => (
              <motion.section
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4 }}
                className="scroll-mt-24 rounded-3xl border border-border/60 bg-card/40 p-6 backdrop-blur-sm sm:p-8"
              >
                <header className="mb-5 flex items-start gap-4">
                  <div
                    className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${accent} text-white shadow-lg`}
                  >
                    <s.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span
                        className={`text-[11px] font-black tracking-widest ${accentSoft.split(" ").slice(1).join(" ")}`}
                      >
                        بند {toPersianDigits(String(i + 1).padStart(2, "0"))}
                      </span>
                    </div>
                    <h2 className="mt-1 text-xl font-black leading-tight sm:text-2xl">{s.title}</h2>
                  </div>
                </header>

                {s.highlight && (
                  <div
                    className={`mb-4 rounded-2xl border border-border/60 ${accentSoft} p-3 text-[12.5px] font-bold leading-relaxed`}
                  >
                    {s.highlight}
                  </div>
                )}

                <div className="prose-legal text-[14.5px] leading-[2] text-foreground/90">
                  {s.body}
                </div>
              </motion.section>
            ))}

            {/* Acceptance + contact CTA */}
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-primary/10 via-card/40 to-secondary-main/10 p-6 sm:p-8"
            >
              <div className="grid items-center gap-6 sm:grid-cols-[1fr_auto]">
                <div>
                  <h3 className="text-lg font-black sm:text-xl">سؤال یا درخواستی دارید؟</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    تیم پشتیبانی ترندی اسمارت پاسخگوی پرسش‌های حقوقی، حریم خصوصی و درخواست‌های
                    مدیریت داده‌های شماست.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="mailto:legal@trendi-smart.ir"
                    className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-l ${accent} px-4 py-2 text-xs font-black text-white shadow-lg`}
                  >
                    <Mail className="h-3.5 w-3.5" />
                    ارتباط با ما
                  </a>
                  <Link
                    href={sisterHref}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/60 px-4 py-2 text-xs font-bold text-foreground backdrop-blur-md transition hover:border-primary/40 hover:text-primary"
                  >
                    {sisterTitle}
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </motion.section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-card/30">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-xs text-muted-foreground sm:flex-row sm:px-6 lg:px-8">
          <span>
            © {toPersianDigits(new Date().getFullYear())} ترندی اسمارت — تمام حقوق محفوظ است.
          </span>
          <div className="flex items-center gap-3">
            <Link href="/privacy" className="hover:text-primary">
              حریم خصوصی
            </Link>
            <span className="opacity-40">·</span>
            <Link href="/terms" className="hover:text-primary">
              قوانین
            </Link>
            <span className="opacity-40">·</span>
            <Link href="/" className="hover:text-primary">
              صفحه اصلی
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Chip({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 font-bold text-foreground/80 backdrop-blur-md">
      {icon}
      {label}
    </span>
  );
}

/** Pretty bullet list helper for section bodies */
export function LegalList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-primary to-secondary-main" />
          <div className="flex-1">{it}</div>
        </li>
      ))}
    </ul>
  );
}

/** Numbered list (for sub-clauses) */
export function LegalNumbered({ items }: { items: ReactNode[] }) {
  return (
    <ol className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-muted text-[11px] font-black text-foreground">
            {toPersianDigits(i + 1)}
          </span>
          <div className="flex-1 pt-0.5">{it}</div>
        </li>
      ))}
    </ol>
  );
}
